import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './PrixCatalogue.css';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function calculerPrix(item, nb_personnes = 50, quantite = 1) {
  if (!item) return { prix_unitaire: 0, sous_total: 0, prix_minimum_applique: false };
  const { type_prix, prix_base, paliers_prix, prix_minimum } = item;
  let prix_calcule = 0;

  if (type_prix === 'par_personne') {
    prix_calcule = (prix_base || 0) * nb_personnes;
  } else if (type_prix === 'degressif') {
    const paliers = Array.isArray(paliers_prix) ? paliers_prix : [];
    const palier  = paliers.find(p =>
      nb_personnes >= p.nb_min &&
      (p.nb_max === null || nb_personnes <= p.nb_max)
    );
    prix_calcule = (palier?.prix ?? prix_base ?? 0) * nb_personnes;
  } else if (type_prix === 'par_unite' || type_prix === 'horaire') {
    prix_calcule = Math.abs(prix_base || 0) * quantite;
  }

  const minimum_applique = prix_minimum && prix_calcule < prix_minimum;
  if (minimum_applique) prix_calcule = prix_minimum;

  const paliers      = Array.isArray(paliers_prix) ? paliers_prix : [];
  const palier_actif = type_prix === 'degressif'
    ? paliers.find(p => nb_personnes >= p.nb_min && (p.nb_max === null || nb_personnes <= p.nb_max))
    : null;

  return {
    prix_unitaire: palier_actif?.prix ?? Math.abs(prix_base || 0),
    sous_total:    prix_calcule,
    prix_minimum_applique: !!minimum_applique,
  };
}

function fmt(n) { return n == null ? '—' : `${Number(n).toFixed(2)} $`; }

const CATEGORIES = [
  { id: 'plateau',     label: 'Plateaux',          iconName: 'repas' },
  { id: 'transport',   label: 'Transport',          iconName: 'transport' },
  { id: 'location',    label: 'Location',           iconName: 'package' },
  { id: 'maindoeuvre', label: "Main d'oeuvre",      iconName: 'utilisateur' },
  { id: 'extra',       label: 'Extras',             iconName: 'etiquette' },
  { id: 'rabais',      label: 'Rabais',             iconName: 'argent' },
  { id: 'menus',       label: 'Menus enregistrés',  iconName: 'liste' },
];

const TYPE_PRIX_LABELS = {
  degressif:    'Dégressif',
  par_personne: 'Par personne',
  par_unite:    'Par unité',
  horaire:      'Horaire',
};

const UNITE_OPTIONS = ['personne', 'forfait', 'km', 'heure', 'journée', 'unité'];

// ─── COMPOSANT SIMULATEUR ─────────────────────────────────────────────────────

function Simulateur({ item }) {
  const [nb,  setNb]  = useState(50);
  const [qte, setQte] = useState(1);
  const isParPersonne = item.type_prix === 'par_personne' || item.type_prix === 'degressif';
  const result        = calculerPrix(item, nb, qte);

  return (
    <div className="pc-simulateur">
      <span className="pc-sim-label muted">Simuler pour</span>
      {isParPersonne ? (
        <input
          type="number"
          className="pc-sim-input"
          value={nb}
          onChange={e => setNb(Math.max(1, parseInt(e.target.value) || 1))}
          min={1}
        />
      ) : (
        <input
          type="number"
          className="pc-sim-input"
          value={qte}
          onChange={e => setQte(Math.max(1, parseInt(e.target.value) || 1))}
          min={1}
        />
      )}
      <span className="pc-sim-unite muted">
        {isParPersonne ? 'pers.' : (item.unite_label || 'unités')}
      </span>
      <span className="pc-sim-result">
        {isParPersonne && (
          <span className="muted" style={{ fontSize: 12 }}>{result.prix_unitaire.toFixed(2)} $/pers. = </span>
        )}
        <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1", fontFamily: 'Cinzel, serif' }}>
          {fmt(result.sous_total)}
        </strong>
        {result.prix_minimum_applique && (
          <span className="badge badge--warning" style={{ marginLeft: 6, fontSize: 10 }}>minimum garanti</span>
        )}
      </span>
    </div>
  );
}

// ─── COMPOSANT CARD ITEM CATALOGUE ───────────────────────────────────────────

function CardItem({ item, onEdit, onDelete, onToggle }) {
  return (
    <div className={`pc-card card rise-in ${!item.actif ? 'pc-card--inactif' : ''}`}>
      <div className="card-header">
        <div className="card-header-left" style={{ minWidth: 0 }}>
          <span className="card-header-icon">
            <Icon name="etiquette" size={15} />
          </span>
          <span className="card-title" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.nom}
          </span>
          <span className="badge">{TYPE_PRIX_LABELS[item.type_prix]}</span>
          {!item.actif && <span className="badge">Inactif</span>}
        </div>
        <div className="pc-card-actions">
          <button
            className="btn btn-ghost btn--sm"
            onClick={() => onEdit(item)}
            title="Modifier"
            aria-label="Modifier"
          >
            <Icon name="modifier" size={14} />
          </button>
          <button
            className="btn btn-ghost btn--sm"
            onClick={() => onToggle(item)}
            title={item.actif ? 'Désactiver' : 'Activer'}
            aria-label={item.actif ? 'Désactiver' : 'Activer'}
          >
            <Icon name={item.actif ? 'arreter' : 'demarrer'} size={14} />
          </button>
          <button
            className="btn btn-danger btn--sm"
            onClick={() => onDelete(item)}
            title="Supprimer"
            aria-label="Supprimer"
          >
            <Icon name="fermer" size={14} />
          </button>
        </div>
      </div>

      {item.description && (
        <p className="pc-card-desc muted">{item.description}</p>
      )}

      {/* Contenu plateau */}
      {item.contenu_plateau && item.contenu_plateau.length > 0 && (
        <div className="pc-contenu">
          {item.contenu_plateau.map((c, i) => (
            <span key={i} className="pc-contenu-chip">
              {c.quantite} {c.unite} {c.label_affichage || c.produit}
            </span>
          ))}
        </div>
      )}

      {/* Sides */}
      {item.sides_inclus && item.sides_inclus.length > 0 && (
        <div className="pc-sides muted">
          <span style={{ fontWeight: 600 }}>Sides : </span>
          {item.sides_inclus.join(', ')}
        </div>
      )}

      {/* Paliers dégressifs */}
      {item.type_prix === 'degressif' && item.paliers_prix && (
        <div className="pc-paliers">
          <div className="pc-paliers-titre">Tarification dégresssive</div>
          {item.paliers_prix.map((p, i) => (
            <div key={i} className="pc-palier-ligne">
              <span className="pc-palier-range muted">
                {p.nb_min}–{p.nb_max ? p.nb_max : '∞'} pers.
              </span>
              <span className="pc-palier-prix gold" style={{ fontFeatureSettings: "'tnum' 1" }}>
                {p.prix.toFixed(2)} $/pers.
              </span>
            </div>
          ))}
          {item.prix_minimum && (
            <div className="pc-palier-minimum">
              Prix minimum garanti : <strong>{fmt(item.prix_minimum)}</strong>
            </div>
          )}
        </div>
      )}

      {/* Prix simple */}
      {item.type_prix !== 'degressif' && (
        <div className="pc-prix-simple">
          <span className="gold" style={{ fontFamily: 'Cinzel, serif', fontFeatureSettings: "'tnum' 1" }}>
            {Math.abs(item.prix_base || 0).toFixed(2)} $
          </span>
          <span className="muted"> / {item.unite_label}</span>
        </div>
      )}

      <Simulateur item={item} />
    </div>
  );
}

// ─── COMPOSANT CARD MENU ─────────────────────────────────────────────────────

function CardMenu({ menu, nbSim, onEdit, onDelete }) {
  const inclus    = menu.items.filter(it => it.inclus);
  const optionnel = menu.items.filter(it => !it.inclus);

  const totalSim = inclus.reduce((sum, it) => {
    if (!it.catalogue) return sum;
    return sum + calculerPrix(it.catalogue, nbSim).sous_total;
  }, 0);

  return (
    <div className="pc-card card pc-card-menu rise-in">
      <div className="card-header">
        <div className="card-header-left">
          <span className="card-header-icon">
            <Icon name="liste" size={15} />
          </span>
          <span className="card-title">{menu.nom}</span>
          <span className="badge badge--info">{menu.type_menu}</span>
        </div>
        <div className="pc-card-actions">
          <button
            className="btn btn-ghost btn--sm"
            onClick={() => onEdit(menu)}
            title="Modifier"
            aria-label="Modifier"
          >
            <Icon name="modifier" size={14} />
          </button>
          <button
            className="btn btn-danger btn--sm"
            onClick={() => onDelete(menu)}
            title="Supprimer"
            aria-label="Supprimer"
          >
            <Icon name="fermer" size={14} />
          </button>
        </div>
      </div>

      {menu.description && (
        <p className="pc-card-desc muted">{menu.description}</p>
      )}

      {inclus.length > 0 && (
        <div className="pc-menu-section">
          <div className="pc-menu-section-titre">Inclus</div>
          {inclus.map(it => (
            <div key={it.id} className="pc-menu-item">
              <Icon name="check" size={13} color="var(--success)" />
              <span>{it.catalogue?.nom || it.description_override || '—'}</span>
              {it.catalogue && (
                <span className="muted" style={{ fontSize: 11 }}>
                  {it.catalogue.type_prix === 'degressif'
                    ? `dégressif ${it.catalogue.prix_base}→${Math.min(...(it.catalogue.paliers_prix || []).map(p => p.prix))} $/pers.`
                    : `${Math.abs(it.catalogue.prix_base)} $/${it.catalogue.unite_label}`}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {optionnel.length > 0 && (
        <div className="pc-menu-section">
          <div className="pc-menu-section-titre">Optionnels</div>
          {optionnel.map(it => (
            <div key={it.id} className="pc-menu-item pc-menu-item--opt">
              <Icon name="chevron" size={13} />
              <span>{it.catalogue?.nom || it.description_override || '—'}</span>
              {it.catalogue && (
                <span className="muted" style={{ fontSize: 11 }}>
                  ({Math.abs(it.catalogue.prix_base)} $/{it.catalogue.unite_label})
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="pc-menu-sim">
        Pour <strong>{nbSim}</strong> personnes (inclus) :
        <span className="gold" style={{ fontFamily: 'Cinzel, serif', marginLeft: 4, fontFeatureSettings: "'tnum' 1" }}>
          {fmt(totalSim)}
        </span>
        {optionnel.length > 0 && <span className="muted"> + options</span>}
      </div>
    </div>
  );
}

// ─── MODAL ITEM CATALOGUE ─────────────────────────────────────────────────────

function ModalItem({ item, onSave, onClose }) {
  const [form, setForm] = useState({
    nom:             item?.nom             || '',
    description:     item?.description     || '',
    categorie:       item?.categorie       || 'plateau',
    sous_categorie:  item?.sous_categorie  || '',
    type_prix:       item?.type_prix       || 'degressif',
    prix_base:       item?.prix_base       ?? '',
    unite_label:     item?.unite_label     || 'personne',
    prix_minimum:    item?.prix_minimum    ?? '',
    contenu_plateau: item?.contenu_plateau || [],
    paliers_prix:    item?.paliers_prix    || [{ nb_min: 1, nb_max: 50, prix: 40 }],
    sides_inclus:    (item?.sides_inclus   || []).join('\n'),
    emoji:           item?.emoji           || '',
    ordre:           item?.ordre           ?? 0,
    notes_internes:  item?.notes_internes  || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function ajouterPalier() {
    const last = form.paliers_prix[form.paliers_prix.length - 1];
    const min  = last ? (last.nb_max ? last.nb_max + 1 : 200) : 1;
    set('paliers_prix', [...form.paliers_prix, { nb_min: min, nb_max: null, prix: 30 }]);
  }

  function majPalier(i, k, v) {
    const p = [...form.paliers_prix];
    p[i] = { ...p[i], [k]: v === '' || v === null ? null : Number(v) };
    set('paliers_prix', p);
  }

  function supprimerPalier(i) {
    set('paliers_prix', form.paliers_prix.filter((_, j) => j !== i));
  }

  function ajouterContenu() {
    set('contenu_plateau', [...form.contenu_plateau, { produit: '', quantite: 1, unite: 'g', label_affichage: '' }]);
  }

  function majContenu(i, k, v) {
    const c = [...form.contenu_plateau];
    c[i] = { ...c[i], [k]: k === 'quantite' ? Number(v) : v };
    set('contenu_plateau', c);
  }

  function supprimerContenu(i) {
    set('contenu_plateau', form.contenu_plateau.filter((_, j) => j !== i));
  }

  async function sauvegarder(e) {
    e.preventDefault();
    if (!form.nom || !form.categorie) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        prix_base:    form.prix_base    !== '' ? Number(form.prix_base)    : 0,
        prix_minimum: form.prix_minimum !== '' ? Number(form.prix_minimum) : null,
        sides_inclus: form.sides_inclus
          ? form.sides_inclus.split('\n').map(s => s.trim()).filter(Boolean)
          : [],
        paliers_prix:    form.type_prix === 'degressif' ? form.paliers_prix : null,
        contenu_plateau: form.contenu_plateau.length > 0 ? form.contenu_plateau : null,
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{item ? 'Modifier un item de prix' : 'Nouvel item de prix'}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={16} />
          </button>
        </div>
        <form onSubmit={sauvegarder} className="modal-form">
          <div className="form-row">
            <div className="form-group" style={{ flex: 3 }}>
              <label>Nom *</label>
              <input value={form.nom} onChange={e => set('nom', e.target.value)} required />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Emoji (optionnel)</label>
              <input value={form.emoji} onChange={e => set('emoji', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Catégorie *</label>
              <select value={form.categorie} onChange={e => set('categorie', e.target.value)}>
                {CATEGORIES.filter(c => c.id !== 'menus').map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Type de prix</label>
              <select value={form.type_prix} onChange={e => set('type_prix', e.target.value)}>
                <option value="degressif">Dégressif (par personne)</option>
                <option value="par_personne">Prix fixe par personne</option>
                <option value="par_unite">Prix fixe par unité</option>
                <option value="horaire">Horaire</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <input value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          {/* Prix de base */}
          <div className="form-row">
            <div className="form-group">
              <label>Prix de base ($)</label>
              <input type="number" step="0.01" value={form.prix_base}
                onChange={e => set('prix_base', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Unité (label)</label>
              <input list="unite-options" value={form.unite_label}
                onChange={e => set('unite_label', e.target.value)} />
              <datalist id="unite-options">
                {UNITE_OPTIONS.map(u => <option key={u} value={u} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label>Prix minimum garanti ($)</label>
              <input type="number" step="0.01" value={form.prix_minimum}
                placeholder="Sans minimum"
                onChange={e => set('prix_minimum', e.target.value)} />
            </div>
          </div>

          {/* Paliers dégressifs */}
          {form.type_prix === 'degressif' && (
            <div className="form-group">
              <label>Paliers de prix</label>
              <table className="esc-table pc-paliers-table">
                <thead>
                  <tr>
                    <th>De (pers.)</th>
                    <th>À (pers.)</th>
                    <th>Prix/pers. ($)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {form.paliers_prix.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <input type="number" value={p.nb_min}
                          onChange={e => majPalier(i, 'nb_min', e.target.value)} />
                      </td>
                      <td>
                        <input type="number" value={p.nb_max ?? ''}
                          placeholder="illimité"
                          onChange={e => majPalier(i, 'nb_max', e.target.value || null)} />
                      </td>
                      <td>
                        <input type="number" step="0.01" value={p.prix}
                          onChange={e => majPalier(i, 'prix', e.target.value)} />
                      </td>
                      <td>
                        <button type="button" className="btn btn-danger btn--sm"
                          onClick={() => supprimerPalier(i)} aria-label="Supprimer palier">
                          <Icon name="fermer" size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="btn btn-ghost btn--sm" style={{ marginTop: 'var(--space-2)' }}
                onClick={ajouterPalier}>
                <Icon name="ajouter" size={14} />
                Ajouter un palier
              </button>
            </div>
          )}

          {/* Contenu plateau */}
          {form.categorie === 'plateau' && (
            <div className="form-group">
              <label>Contenu du plateau</label>
              {form.contenu_plateau.map((c, i) => (
                <div key={i} className="pc-contenu-ligne form-row" style={{ alignItems: 'center', gap: 6 }}>
                  <input placeholder="produit" value={c.produit}
                    style={{ flex: 2 }}
                    onChange={e => majContenu(i, 'produit', e.target.value)} />
                  <input type="number" placeholder="qté" value={c.quantite}
                    style={{ flex: 1 }}
                    onChange={e => majContenu(i, 'quantite', e.target.value)} />
                  <input placeholder="unité" value={c.unite}
                    style={{ flex: 1 }}
                    onChange={e => majContenu(i, 'unite', e.target.value)} />
                  <input placeholder="label affiché" value={c.label_affichage}
                    style={{ flex: 3 }}
                    onChange={e => majContenu(i, 'label_affichage', e.target.value)} />
                  <button type="button" className="btn btn-danger btn--sm"
                    onClick={() => supprimerContenu(i)} aria-label="Supprimer">
                    <Icon name="fermer" size={13} />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-ghost btn--sm" style={{ marginTop: 'var(--space-2)' }}
                onClick={ajouterContenu}>
                <Icon name="ajouter" size={14} />
                Ajouter item
              </button>
            </div>
          )}

          {/* Sides */}
          {form.categorie === 'plateau' && (
            <div className="form-group">
              <label>Sides inclus (un par ligne)</label>
              <textarea rows={4} value={form.sides_inclus}
                onChange={e => set('sides_inclus', e.target.value)}
                placeholder="Salade de brocoli&#10;Mac n cheese&#10;Sauce BBQ maison" />
            </div>
          )}

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Ordre d'affichage</label>
              <input type="number" value={form.ordre}
                onChange={e => set('ordre', Number(e.target.value))} />
            </div>
            <div className="form-group" style={{ flex: 3 }}>
              <label>Notes internes</label>
              <input value={form.notes_internes}
                onChange={e => set('notes_internes', e.target.value)} />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── MODAL MENU ENREGISTRÉ ────────────────────────────────────────────────────

function ModalMenu({ menu, catalogue, onSave, onClose }) {
  const [form, setForm] = useState({
    nom:         menu?.nom         || '',
    emoji:       menu?.emoji       || '',
    type_menu:   menu?.type_menu   || 'plateau',
    description: menu?.description || '',
    notes:       menu?.notes       || '',
    items: menu?.items?.map(it => ({
      prix_catalogue_id:    it.prix_catalogue_id,
      description_override: it.description_override || '',
      quantite:             it.quantite ?? 1,
      inclus:               !!it.inclus,
      ordre:                it.ordre ?? 0,
    })) || [],
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function ajouterItem(id_catalogue) {
    const it = catalogue.find(c => c.id === id_catalogue);
    if (!it) return;
    set('items', [...form.items, {
      prix_catalogue_id: it.id, description_override: '', quantite: 1, inclus: true, ordre: form.items.length,
    }]);
  }

  function majItem(i, k, v) {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    set('items', items);
  }

  function supprimerItem(i) {
    set('items', form.items.filter((_, j) => j !== i));
  }

  async function sauvegarder(e) {
    e.preventDefault();
    if (!form.nom) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  }

  const disponibles = catalogue.filter(c => !form.items.some(it => it.prix_catalogue_id === c.id));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{menu ? 'Modifier le menu' : 'Nouveau menu enregistré'}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={16} />
          </button>
        </div>
        <form onSubmit={sauvegarder} className="modal-form">
          <div className="form-row">
            <div className="form-group" style={{ flex: 3 }}>
              <label>Nom *</label>
              <input value={form.nom} onChange={e => set('nom', e.target.value)} required />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Emoji (optionnel)</label>
              <input value={form.emoji} onChange={e => set('emoji', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Type</label>
              <select value={form.type_menu} onChange={e => set('type_menu', e.target.value)}>
                <option value="plateau">Plateau</option>
                <option value="sandwich">Sandwich</option>
                <option value="libre">Libre</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description client</label>
            <input value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Items du menu</label>
            {form.items.length === 0 && (
              <p className="muted" style={{ fontSize: 13 }}>Aucun item. Ajoutez des éléments ci-dessous.</p>
            )}
            {form.items.map((it, i) => {
              const cat = catalogue.find(c => c.id === it.prix_catalogue_id);
              return (
                <div key={i} className="pc-menu-item-ligne">
                  <Icon name="liste" size={14} color="var(--muted)" />
                  <span className="pc-menu-item-nom">{cat?.nom || it.description_override || '—'}</span>
                  <label className="pc-menu-item-inclus">
                    <input type="checkbox" checked={it.inclus}
                      onChange={e => majItem(i, 'inclus', e.target.checked)} />
                    Inclus
                  </label>
                  <button type="button" className="btn btn-danger btn--sm"
                    onClick={() => supprimerItem(i)} aria-label="Supprimer">
                    <Icon name="fermer" size={13} />
                  </button>
                </div>
              );
            })}

            {disponibles.length > 0 && (
              <div className="form-group" style={{ marginTop: 'var(--space-3)' }}>
                <label>Ajouter depuis le catalogue</label>
                <select defaultValue=""
                  onChange={e => { if (e.target.value) ajouterItem(Number(e.target.value)); e.target.value = ''; }}>
                  <option value="">— Sélectionner un item —</option>
                  {disponibles.map(c => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Notes internes</label>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────────

export default function PrixCatalogue() {
  const [items,   setItems]   = useState([]);
  const [menus,   setMenus]   = useState([]);
  const [onglet,  setOnglet]  = useState('plateau');
  const [loading, setLoading] = useState(true);
  const [erreur,  setErreur]  = useState('');
  const [toast,   setToast]   = useState('');
  const [nbSim,   setNbSim]   = useState(50);

  const [showItemModal, setShowItemModal] = useState(false);
  const [editItem,      setEditItem]      = useState(null);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editMenu,      setEditMenu]      = useState(null);

  function showToast(texte) {
    setToast(texte);
    setTimeout(() => setToast(''), 4000);
  }

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur('');
      const [pc, mc] = await Promise.all([
        api.get('/prix-catalogue'),
        api.get('/menus-corpo'),
      ]);
      setItems(pc || []);
      setMenus(mc || []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // ─── Item catalogue ──────────────────────────────────────────────────────────

  async function sauvegarderItem(payload) {
    try {
      if (editItem) {
        await api.put(`/prix-catalogue/${editItem.id}`, payload);
        showToast('Item mis à jour');
      } else {
        await api.post('/prix-catalogue', payload);
        showToast('Item créé');
      }
      setShowItemModal(false);
      setEditItem(null);
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function supprimerItem(item) {
    if (!window.confirm(`Supprimer « ${item.nom} » ?`)) return;
    try {
      await api.delete(`/prix-catalogue/${item.id}`);
      showToast('Item supprimé');
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function toggleItem(item) {
    try {
      await api.patch(`/prix-catalogue/${item.id}/toggle`);
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  // ─── Menu enregistré ─────────────────────────────────────────────────────────

  async function sauvegarderMenu(payload) {
    try {
      if (editMenu) {
        await api.put(`/menus-corpo/${editMenu.id}`, payload);
        showToast('Menu mis à jour');
      } else {
        await api.post('/menus-corpo', payload);
        showToast('Menu créé');
      }
      setShowMenuModal(false);
      setEditMenu(null);
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function supprimerMenu(menu) {
    if (!window.confirm(`Supprimer le menu « ${menu.nom} » ?`)) return;
    try {
      await api.delete(`/menus-corpo/${menu.id}`);
      showToast('Menu supprimé');
      charger();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  const itemsOnglet = items.filter(it => onglet !== 'menus' && it.categorie === onglet);

  // ── Skeleton loading ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pc-page">
        <div className="pc-page-header">
          <div>
            <div className="skeleton" style={{ height: 36, width: 300, marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 12, width: 200 }} />
          </div>
          <div className="skeleton" style={{ height: 36, width: 140, borderRadius: 'var(--radius-md)' }} />
        </div>
        <div className="pc-tabs">
          {CATEGORIES.map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 36, width: 90, borderRadius: 'var(--radius-md)' }} />
          ))}
        </div>
        <div className="pc-grid">
          {[0,1,2,3].map(i => (
            <div key={i} className="card" style={{ height: 220 }}>
              <div className="skeleton" style={{ height: '100%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pc-page">
      {/* Toast */}
      {toast && (
        <div className="pc-toast rise-in">
          <Icon name="succes" size={15} />
          {toast}
        </div>
      )}

      {/* En-tête */}
      <div className="pc-page-header">
        <div className="pc-page-header-text">
          <h1 className="page-title">Catalogue de prix &amp; Menus</h1>
          <p className="pc-subtitle muted">
            Gérez vos tarifs et menus préenregistrés pour les soumissions corporatives
          </p>
        </div>
        <div className="pc-header-actions">
          {/* Simulateur global */}
          <div className="pc-sim-global">
            <Icon name="auto" size={14} color="var(--muted)" />
            <span className="muted" style={{ fontSize: 13 }}>Simuler pour</span>
            <input
              type="number"
              className="pc-sim-global-input"
              value={nbSim}
              min={1}
              onChange={e => setNbSim(Math.max(1, parseInt(e.target.value) || 1))}
            />
            <span className="muted" style={{ fontSize: 13 }}>pers.</span>
          </div>
          {onglet !== 'menus' && (
            <button
              className="btn btn-primary"
              onClick={() => { setEditItem(null); setShowItemModal(true); }}
            >
              <Icon name="ajouter" size={15} />
              Nouveau prix
            </button>
          )}
          {onglet === 'menus' && (
            <button
              className="btn btn-primary"
              onClick={() => { setEditMenu(null); setShowMenuModal(true); }}
            >
              <Icon name="ajouter" size={15} />
              Nouveau menu
            </button>
          )}
        </div>
      </div>

      {/* Erreur */}
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-5)' }}>
          <Icon name="erreur" size={16} />
          {erreur}
        </div>
      )}

      {/* Onglets catégories */}
      <div className="pc-tabs">
        {CATEGORIES.map(cat => {
          const count = cat.id === 'menus'
            ? menus.length
            : items.filter(it => it.categorie === cat.id).length;
          return (
            <button
              key={cat.id}
              className={`pc-tab ${onglet === cat.id ? 'pc-tab--active' : ''}`}
              onClick={() => setOnglet(cat.id)}
            >
              <Icon name={cat.iconName} size={14} />
              {cat.label}
              <span className={`pc-tab-count ${onglet === cat.id ? 'pc-tab-count--active' : ''}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Contenu */}
      {onglet === 'menus' ? (
        menus.length === 0 ? (
          <div className="empty-state">
            <Icon name="liste" size={40} />
            <span className="empty-state__titre">Aucun menu enregistré</span>
            <span>Cliquez « Nouveau menu » pour en créer un.</span>
          </div>
        ) : (
          <div className="pc-grid">
            {menus.map(m => (
              <CardMenu
                key={m.id}
                menu={m}
                nbSim={nbSim}
                onEdit={menu => { setEditMenu(menu); setShowMenuModal(true); }}
                onDelete={supprimerMenu}
              />
            ))}
          </div>
        )
      ) : (
        itemsOnglet.length === 0 ? (
          <div className="empty-state">
            <Icon name="etiquette" size={40} />
            <span className="empty-state__titre">Aucun item dans cette catégorie</span>
            <span>Cliquez « Nouveau prix » pour en créer un.</span>
          </div>
        ) : (
          <div className="pc-grid">
            {itemsOnglet.map(item => (
              <CardItem
                key={item.id}
                item={item}
                onEdit={it => { setEditItem(it); setShowItemModal(true); }}
                onDelete={supprimerItem}
                onToggle={toggleItem}
              />
            ))}
          </div>
        )
      )}

      {/* Modal item */}
      {showItemModal && (
        <ModalItem
          item={editItem}
          onSave={sauvegarderItem}
          onClose={() => { setShowItemModal(false); setEditItem(null); }}
        />
      )}

      {/* Modal menu */}
      {showMenuModal && (
        <ModalMenu
          menu={editMenu}
          catalogue={items.filter(it => it.actif)}
          onSave={sauvegarderMenu}
          onClose={() => { setShowMenuModal(false); setEditMenu(null); }}
        />
      )}
    </div>
  );
}
